/**
 * Settings → Telemetría — export de logs del tenant (REQ-LOG-001/002, ADR-143).
 *
 * N1 es platform-managed (storage-api). N2 es un bucket del cliente (AWS S3,
 * Azure Blob o GCP) y la credencial se selecciona desde el Secret Vault; nunca
 * se muestra ni se envía al cliente.
 */

import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button, Card, Spinner } from '@/components/ui';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { useI18n } from '@/i18n';
import { getErrorMessage } from '@/lib/api-client';
import {
  useTelemetryActions,
  useTelemetryConfig,
  useTelemetryDeliveries,
  useTelemetryHealth,
  useTelemetryIntegrations,
  useTenantSecrets,
  useTestTelemetryDestination,
  useUpdateTelemetryConfig,
  type N2Destination,
  type TelemetryExportConfig,
} from '../hooks';

type Provider = N2Destination['provider'];

export function parseEventTypes(raw: string): string[] {
  const seen = new Set<string>();
  for (const part of raw.split(',')) {
    const value = part.trim();
    if (value) seen.add(value);
  }
  return [...seen];
}

export function buildDestination(
  provider: Provider,
  values: {
    bucket: string;
    region: string;
    accountUrl: string;
    container: string;
    authMode: string;
    roleArn: string;
    prefix: string;
  },
): N2Destination {
  const base = { provider, auth_mode: values.authMode, prefix: values.prefix || 'logs/' };
  if (provider === 'aws_s3') {
    return {
      ...base,
      bucket: values.bucket.trim(),
      region: values.region.trim(),
      ...(values.authMode === 'assume_role'
        ? { role_arn: values.roleArn.trim() }
        : {}),
    };
  }
  if (provider === 'azure_blob') {
    return {
      ...base,
      account_url: values.accountUrl.trim(),
      container: values.container.trim(),
    };
  }
  return { ...base, bucket: values.bucket.trim() };
}

export function isSaveable(
  config: Partial<TelemetryExportConfig>,
  destination?: N2Destination | null,
): boolean {
  if (!(config.prefix ?? '').trim()) return false;
  if (!destination || !destination.auth_mode) return false;
  if (!(config.credential_secret_id ?? '').trim()) return false;
  if (destination.provider === 'aws_s3') {
    return Boolean(destination.bucket && destination.region);
  }
  if (destination.provider === 'azure_blob') {
    return Boolean(destination.account_url && destination.container);
  }
  return Boolean(destination.bucket);
}

const defaultAuthMode: Record<Provider, string> = {
  aws_s3: 'access_key',
  azure_blob: 'sas',
  gcs: 'service_account',
};

export default function TelemetrySettingsPage() {
  const { t } = useI18n();
  const config = useTelemetryConfig();
  const update = useUpdateTelemetryConfig();
  const testDestination = useTestTelemetryDestination();
  const health = useTelemetryHealth();
  const deliveries = useTelemetryDeliveries();
  const integrations = useTelemetryIntegrations();
  const secrets = useTenantSecrets();
  const actions = useTelemetryActions();

  const [draft, setDraft] = useState<Partial<TelemetryExportConfig>>({});
  const [eventTypes, setEventTypes] = useState('');
  const [initialised, setInitialised] = useState(false);
  const [provider, setProvider] = useState<Provider>('aws_s3');
  const [bucket, setBucket] = useState('');
  const [region, setRegion] = useState('');
  const [accountUrl, setAccountUrl] = useState('');
  const [container, setContainer] = useState('');
  const [authMode, setAuthMode] = useState('access_key');
  const [roleArn, setRoleArn] = useState('');
  const [secretId, setSecretId] = useState('');
  const [prefix, setPrefix] = useState('logs/');

  useEffect(() => {
    if (config.data && !initialised) {
      const exportCfg = config.data.export;
      const destination = exportCfg.destination;
      setDraft(exportCfg);
      setEventTypes(exportCfg.event_types.join(', '));
      setPrefix(exportCfg.prefix || destination?.prefix || 'logs/');
      setSecretId(exportCfg.credential_secret_id ?? '');
      if (destination) {
        setProvider(destination.provider);
        setAuthMode(destination.auth_mode || defaultAuthMode[destination.provider]);
        setBucket(destination.bucket ?? '');
        setRegion(destination.region ?? '');
        setAccountUrl(destination.account_url ?? '');
        setContainer(destination.container ?? '');
        setRoleArn(destination.role_arn ?? '');
      }
      setInitialised(true);
    }
  }, [config.data, initialised]);

  const exportCfg = useMemo<Partial<TelemetryExportConfig>>(
    () => ({ ...(config.data?.export ?? {}), ...draft }),
    [config.data, draft],
  );

  const destination = useMemo(
    () => buildDestination(provider, { bucket, region, accountUrl, container, authMode, roleArn, prefix }),
    [provider, bucket, region, accountUrl, container, authMode, roleArn, prefix],
  );

  const integration = integrations.data?.[0];
  const needsMigration =
    Boolean(exportCfg.migration_required) ||
    (Boolean(exportCfg.enabled) && !config.data?.export?.destination);

  const authModes =
    provider === 'aws_s3'
      ? ['access_key', 'assume_role']
      : provider === 'azure_blob'
        ? ['sas', 'service_principal', 'connection_string']
        : ['service_account'];

  const save = async (): Promise<boolean> => {
    try {
      await update.mutateAsync({
        enabled: Boolean(exportCfg.enabled),
        schema_version: 2,
        prefix: (prefix || 'logs/').trim(),
        event_types: parseEventTypes(eventTypes),
        min_level: exportCfg.min_level ?? 'INFO',
        destination: { ...destination, prefix: (prefix || 'logs/').trim() },
        credential_secret_id: secretId,
      });
      toast.success(t('admin.telemetry.export.saved'));
      return true;
    } catch (error) {
      toast.error(getErrorMessage(error));
      return false;
    }
  };

  const testConnection = async () => {
    const saved = await save();
    if (!saved) return;
    try {
      await testDestination.mutateAsync();
      toast.success(t('admin.telemetry.actions.testDone'));
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const runAction = async (action: 'pause' | 'resume' | 'backfill') => {
    if (!integration) return;
    try {
      await actions[action].mutateAsync(integration.id);
      toast.success(t(`admin.telemetry.actions.${action}Done`));
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <div className="space-y-6 p-6" data-testid="telemetry-settings-page">
      <header>
        <h1 className="text-2xl font-semibold">{t('admin.telemetry.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('admin.telemetry.subtitle')}</p>
      </header>

      {needsMigration && (
        <div
          className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
          data-testid="telemetry-migration-required"
        >
          {t('admin.telemetry.export.migrationRequired')}
        </div>
      )}

      <Card className="space-y-4 p-4">
        <div>
          <h2 className="text-lg font-medium">{t('admin.telemetry.destination.title')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('admin.telemetry.destination.description')}
          </p>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <label className="space-y-1 text-sm">
            <span className="font-medium">{t('admin.telemetry.destination.provider')}</span>
            <select
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={provider}
              onChange={(event) => {
                const next = event.target.value as Provider;
                setProvider(next);
                setAuthMode(defaultAuthMode[next]);
              }}
              data-testid="telemetry-provider"
            >
              <option value="aws_s3">{t('admin.telemetry.destination.providers.aws_s3')}</option>
              <option value="azure_blob">{t('admin.telemetry.destination.providers.azure_blob')}</option>
              <option value="gcs">{t('admin.telemetry.destination.providers.gcs')}</option>
            </select>
          </label>

          {provider === 'azure_blob' ? (
            <label className="space-y-1 text-sm">
              <span className="font-medium">{t('admin.telemetry.destination.accountUrl')}</span>
              <Input value={accountUrl} onChange={(event) => setAccountUrl(event.target.value)} />
            </label>
          ) : (
            <label className="space-y-1 text-sm">
              <span className="font-medium">{t('admin.telemetry.destination.bucket')}</span>
              <Input value={bucket} onChange={(event) => setBucket(event.target.value)} />
            </label>
          )}

          {provider === 'aws_s3' && (
            <label className="space-y-1 text-sm">
              <span className="font-medium">{t('admin.telemetry.destination.region')}</span>
              <Input value={region} onChange={(event) => setRegion(event.target.value)} />
            </label>
          )}
          {provider === 'azure_blob' && (
            <label className="space-y-1 text-sm">
              <span className="font-medium">{t('admin.telemetry.destination.container')}</span>
              <Input value={container} onChange={(event) => setContainer(event.target.value)} />
            </label>
          )}
          <label className="space-y-1 text-sm">
            <span className="font-medium">{t('admin.telemetry.destination.prefix')}</span>
            <Input value={prefix} onChange={(event) => setPrefix(event.target.value)} />
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-medium">{t('admin.telemetry.destination.authMode')}</span>
            <select
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={authMode}
              onChange={(event) => setAuthMode(event.target.value)}
            >
              {authModes.map((mode) => (
                <option key={mode} value={mode}>
                  {t(`admin.telemetry.destination.authModes.${mode}`)}
                </option>
              ))}
            </select>
          </label>
          {provider === 'aws_s3' && authMode === 'assume_role' && (
            <label className="space-y-1 text-sm">
              <span className="font-medium">{t('admin.telemetry.destination.roleArn')}</span>
              <Input value={roleArn} onChange={(event) => setRoleArn(event.target.value)} />
            </label>
          )}
          <label className="space-y-1 text-sm md:col-span-2">
            <span className="font-medium">{t('admin.telemetry.destination.secret')}</span>
            <select
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={secretId}
              onChange={(event) => setSecretId(event.target.value)}
              data-testid="telemetry-secret"
            >
              <option value="">{t('admin.telemetry.destination.secretPlaceholder')}</option>
              {(secrets.data ?? []).map((secret) => (
                <option key={secret.id} value={secret.id}>
                  {secret.name}
                </option>
              ))}
            </select>
            {!secrets.isLoading && (secrets.data ?? []).length === 0 && (
              <span className="text-xs text-muted-foreground">
                {t('admin.telemetry.destination.noSecrets')}
              </span>
            )}
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => void testConnection()}
            disabled={testDestination.isPending || !isSaveable({ ...exportCfg, credential_secret_id: secretId, prefix }, destination)}
          >
            {t('admin.telemetry.destination.test')}
          </Button>
        </div>
      </Card>

      <Card className="space-y-4 p-4">
        <div>
          <h2 className="text-lg font-medium">{t('admin.telemetry.export.title')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('admin.telemetry.export.description')}
          </p>
        </div>
        {config.isLoading ? (
          <Spinner />
        ) : (
          <div className="space-y-4">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={Boolean(exportCfg.enabled)}
                onCheckedChange={(checked) =>
                  setDraft((previous) => ({
                    ...previous,
                    enabled: checked === true,
                  }))
                }
              />
              {t('admin.telemetry.export.enabled')}
            </label>
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="telemetry-events">
                {t('admin.telemetry.export.eventTypes')}
              </label>
              <Input
                id="telemetry-events"
                placeholder={t('admin.telemetry.export.eventTypesPlaceholder')}
                value={eventTypes}
                onChange={(event) => setEventTypes(event.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="telemetry-level">
                {t('admin.telemetry.export.minLevel')}
              </label>
              <select
                id="telemetry-level"
                className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={exportCfg.min_level ?? 'INFO'}
                onChange={(event) =>
                  setDraft((previous) => ({
                    ...previous,
                    min_level: event.target.value,
                  }))
                }
              >
                {['DEBUG', 'INFO', 'WARNING', 'ERROR', 'CRITICAL'].map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
            </div>
            <Button
              onClick={() => void save()}
              disabled={
                update.isPending ||
                !isSaveable({ ...exportCfg, credential_secret_id: secretId, prefix }, destination)
              }
            >
              {t('admin.telemetry.export.save')}
            </Button>
          </div>
        )}
      </Card>

      <Card className="space-y-4 p-4">
        <h2 className="text-lg font-medium">{t('admin.telemetry.status.title')}</h2>
        {health.isLoading ? (
          <Spinner />
        ) : (
          <dl className="grid grid-cols-2 gap-2 text-sm">
            <dt className="text-muted-foreground">{t('admin.telemetry.status.export')}</dt>
            <dd>
              {health.data?.export_enabled
                ? t('admin.telemetry.status.enabled')
                : t('admin.telemetry.status.disabled')}
            </dd>
            <dt className="text-muted-foreground">
              {t('admin.telemetry.status.consecutiveFailures')}
            </dt>
            <dd>{health.data?.consecutive_failures ?? 0}</dd>
            <dt className="text-muted-foreground">
              {t('admin.telemetry.status.lastSuccess')}
            </dt>
            <dd>{health.data?.last_success_at ?? t('admin.telemetry.status.never')}</dd>
            <dt className="text-muted-foreground">
              {t('admin.telemetry.status.lastFailure')}
            </dt>
            <dd>{health.data?.last_failure_at ?? t('admin.telemetry.status.never')}</dd>
          </dl>
        )}
        {integration ? (
          <div className="flex flex-wrap gap-2">
            {integration.enabled ? (
              <Button variant="outline" onClick={() => void runAction('pause')}>
                {t('admin.telemetry.actions.pause')}
              </Button>
            ) : (
              <Button variant="outline" onClick={() => void runAction('resume')}>
                {t('admin.telemetry.actions.resume')}
              </Button>
            )}
            <Button variant="outline" onClick={() => void runAction('backfill')}>
              {t('admin.telemetry.actions.backfill')}
            </Button>
          </div>
        ) : null}
      </Card>

      <Card className="space-y-3 p-4">
        <h2 className="text-lg font-medium">{t('admin.telemetry.deliveries.title')}</h2>
        {deliveries.isLoading ? (
          <Spinner />
        ) : !Array.isArray(deliveries.data) || deliveries.data.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('admin.telemetry.deliveries.empty')}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="py-1">{t('admin.telemetry.deliveries.status')}</th>
                <th className="py-1">{t('admin.telemetry.deliveries.batch')}</th>
                <th className="py-1">{t('admin.telemetry.deliveries.attempts')}</th>
                <th className="py-1">{t('admin.telemetry.deliveries.date')}</th>
              </tr>
            </thead>
            <tbody>
              {(Array.isArray(deliveries.data) ? deliveries.data : []).map((delivery, index) => (
                <tr key={`${delivery.created_at}-${index}`} className="border-t">
                  <td className="py-1">
                    {delivery.status === 'success'
                      ? t('admin.telemetry.deliveries.success')
                      : t('admin.telemetry.deliveries.failed')}
                  </td>
                  <td className="py-1">{delivery.batch_size}</td>
                  <td className="py-1">{delivery.attempts}</td>
                  <td className="py-1">{delivery.created_at}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
